import { Waypoint } from 'react-waypoint';

import { Translate } from '@change-corgi/core/react/i18n';
import { Link } from '@change-corgi/design-system-legacy/components/actions';
import { Card } from '@change-corgi/design-system-legacy/components/content';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';
import { iconInfoOutline } from '@change-corgi/design-system-legacy/icons';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import type { WaypointCallbackArgs } from 'src/app/shared/components/layout/lazy';

import { FacebookGroups } from './components/FacebookGroups';
import { RedditSubreddits } from './components/RedditSubreddits';
import { ShareUrl } from './components/ShareUrl';
import { useRecommendations } from './hooks/useRecommendations';
import { useShareRecommendations } from './hooks/useShareRecommendations';

type Props = {
	slug: string;
};

const getTitle = () => (
	<Flex sx={{ alignItems: 'center', flex: '1 0 0', mb: [16, 24] }}>
		<Heading as="h2" size="h4" id="recommendations-title" data-qa="recommendations-title">
			<Translate value="corgi.petition.starter_dashboard_v2.recommendations.title" />
		</Heading>
		<Box
			sx={{
				borderRadius: 4,
				backgroundColor: 'neutral-grey100',
				ml: 12,
			}}
		>
			<Text
				color="secondary-purple"
				fontWeight="bold"
				size="small"
				sx={{
					display: 'flex',
					padding: '2px 8px',
					justifyContent: 'center',
					alignItems: 'center',
					color: 'neutral-grey600',
				}}
			>
				BETA
			</Text>
		</Box>
	</Flex>
);

const tipLink = (actions: ReturnType<typeof useRecommendations>['actions']) => (
	<Box
		sx={{
			display: 'inline',
			mb: '24px',
			textAlign: 'right',
		}}
	>
		<Link
			variant="primary"
			to="#"
			onClick={async (e) => {
				e.preventDefault();
				await actions.handleTipClick();
			}}
			sx={{
				width: ['100%', 'auto'],
				display: 'flex',
				color: 'primary-black',
				fontWeight: '500',
			}}
			data-qa="recommendations-tip-button"
		>
			<Icon icon={iconInfoOutline} color="currentColor" size={18} m={3} />
			<Translate value="corgi.petition.starter_dashboard.recommendations.sharing_tips" />
		</Link>
	</Box>
);

export function RecommendationsControl({ slug }: Props): JSX.Element | null {
	const {
		data: { shareRecommendationsLoading, shouldDisplayShareRecommendations, shareRecommendations },
	} = useShareRecommendations(slug);
	const { actions } = useRecommendations();

	if (shareRecommendationsLoading) {
		return <Loader size="m" my={64} mx="auto" data-testid="share-recommendations-loading" />;
	}

	if (!shouldDisplayShareRecommendations) {
		return null;
	}

	const firstRedditRec = shareRecommendations.reddit.at(0);
	const firstFacebookRec = shareRecommendations.facebook.at(0);

	return (
		<Box as="section" aria-labelledby="recommendations-title">
			<Waypoint
				onPositionChange={(waypointCallbackArgs: WaypointCallbackArgs) => {
					actions.trackView(waypointCallbackArgs, firstFacebookRec?.metadata.source, firstRedditRec?.metadata.source);
				}}
			/>
			<Card p={20} backgroundColor="white" sx={{ border: 'unset', width: '100%', borderRadius: 'big' }}>
				<Flex sx={{ flexDirection: ['column', 'row'] }}>
					{getTitle()}
					{tipLink(actions)}
				</Flex>
				<Box sx={{ position: 'relative' }}>
					{actions.translateHtml('corgi.petition.starter_dashboard.recommendations.description')}&nbsp;🙏
				</Box>
				<Box pt={16} pb={32} sx={{ position: 'relative' }}>
					<ShareUrl />
				</Box>
				<Box
					p={0}
					sx={{
						position: 'relative',
						display: 'grid',
						gridTemplateColumns: ['1fr', '1fr 1fr'],
						columnGap: '40px',
						width: '100%',
						marginBottom: [0, 16],
					}}
				>
					<RedditSubreddits shareRecommendations={shareRecommendations.reddit} />
					<FacebookGroups shareRecommendations={shareRecommendations.facebook} />
				</Box>
				<Text size="small" color="typography-secondary">
					<Translate value="corgi.petition.starter_dashboard.recommendations.footer" />
				</Text>
			</Card>
		</Box>
	);
}
